<script lang="ts">
import { __ } from '@@/bits/intl'
import { NON_STREAM_STANDARD_MAX_POST_SIZE, NON_STREAM_WIDE_MAX_POST_SIZE } from '@@/bits/post_size'
import { isPostPinned } from '@@/bits/post_state'
import { WallWishSortByTypes } from '@@/enums'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceTimelineFormatStore } from '@@/pinia/surface_timeline_format_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Cid, Id, Post } from '@@/types'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import SurfacePopupWithTip from '@@/vuecomponents/SurfacePopupWithTip.vue'
import SurfacePostCompressed from '@@/vuecomponents/SurfacePostCompressed.vue'
import SurfaceTimelineDashLine from '@@/vuecomponents/SurfaceTimelineDashLine.vue'
import SurfaceTimelineDropPostIndicatorLine from '@@/vuecomponents/SurfaceTimelineDropPostIndicatorLine.vue'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

export const TIMELINE_POST_DRAGGABLE_TYPE = 'timeline-post' as const
</script>

<script setup lang="ts">
const props = defineProps<{
  post: Post
  index: number
  isLastPinnedPost: boolean
  reactionIdsByPostId: Record<Id, Id[]>
  commentIdsByPostId: Record<Id, Id[]>
  postsInGroup: Post[]
  isMobileLandscapeScreen: boolean
}>()

const emit = defineEmits<{
  (e: 'dashed-line-id-to-hide', dashedLineIdToHide: Cid | Id | null): void
  (e: 'closest-edge-to-pointer', closestEdgeToPointer: LogicalEdge | null): void
}>()

const { isPostSizeWide, colorScheme, backgroundLuminance, isSectioned } = storeToRefs(useSurfaceStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())
const surfacePostsStore = useSurfacePostsStore()
const { isAPostDragging } = storeToRefs(useSurfacePostsDragAndDropStore())
const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
const {
  dashedLineMarginPercentFromViewportBottom,
  dashedLineMarginBottomClasses,
  adjustedPrimaryBackgroundColorForCircleAndPlusButton,
} = storeToRefs(useSurfaceTimelineFormatStore())

const dashedLineCount = computed(() => (isPostSizeWide.value ? 14 : 9))

const isPinned = computed(() => isPostPinned(props.post))

function shouldMatchApprovalColor(isPublished?: boolean): boolean {
  if (isPublished === true) return false
  return canIModerate.value
}

// #region - Drag and drop
const containerRef = ref<HTMLElement>()
const surfacePopupWithTipRef = ref<InstanceType<typeof SurfacePopupWithTip>>()
const surfacePostWithTipElementRef = computed(() => surfacePopupWithTipRef.value?.$el as HTMLElement | undefined)
const circleButtonRef = ref<InstanceType<typeof OzPlainButton>>()
const circleButtonElementRef = computed(() => circleButtonRef.value?.$el as HTMLElement | undefined)

// Pre-rendered drag preview element to avoid timing issues with attachment processing
const preRenderedDragPreviewRef = ref<InstanceType<typeof SurfacePostCompressed>>()

const SurfacePostDragPreview = computed(() => {
  return preRenderedDragPreviewRef.value?.$el as HTMLElement | undefined
})

// Make the post draggable
const { isDragging: isPostDragging } = useDraggableElement({
  elementRef: surfacePostWithTipElementRef,
  type: TIMELINE_POST_DRAGGABLE_TYPE,
  data: computed(() => ({
    post: props.post, // data to be passed to the drop target
  })),
  isDraggable: computed(() => !shouldDisableDraggingPost.value),
  dragPreview: SurfacePostDragPreview,
  onDragStart: () => {
    isAPostDragging.value = true
  },
  onDrop: () => {
    isAPostDragging.value = false
    emit('dashed-line-id-to-hide', null)
  },
})

// Make the circle button draggable
const { isDragging: isCircleDragging } = useDraggableElement({
  elementRef: circleButtonElementRef,
  type: TIMELINE_POST_DRAGGABLE_TYPE,
  data: computed(() => ({
    post: props.post, // data to be passed to the drop target
  })),
  isDraggable: computed(() => !shouldDisableDraggingPost.value),
  dragPreview: SurfacePostDragPreview,
  onDragStart: () => {
    isAPostDragging.value = true
  },
  onDrop: () => {
    isAPostDragging.value = false
    emit('dashed-line-id-to-hide', null)
  },
})

// Make the post droppable
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef: containerRef,
  type: TIMELINE_POST_DRAGGABLE_TYPE,
  data: computed(() => ({
    post: props.post, // data to be passed to the drop target
  })),
})

const postBeingDraggedOver = ref<Post>()

const canDropPost = computed(() => {
  // Disable drag and drop if the 'Sort by' setting is not 'Drag and drop'
  if (
    surfaceWishArrangementStore.originalWishSortBy !== WallWishSortByTypes.ManualNewPostsFirst &&
    surfaceWishArrangementStore.originalWishSortBy !== WallWishSortByTypes.ManualNewPostsLast
  ) {
    return false
  }

  // Early return if no post is being dragged over
  if (postBeingDraggedOver.value == null) return true

  // Prevent dropping to the same position
  if (props.post.id === postBeingDraggedOver.value.id) return false

  // Enable drop between pinned posts / unpinned posts
  if (isPostPinned(props.post) === isPostPinned(postBeingDraggedOver.value)) return true

  // Prevent unpinned posts from being dropped to the left of a pinned post
  if (closestEdgeToPointer.value === 'start' && isPostPinned(props.post) && !isPostPinned(postBeingDraggedOver.value)) {
    return false
  }

  // Prevent unpinned posts from being dropped between pinned posts
  if (
    closestEdgeToPointer.value === 'end' &&
    isPostPinned(props.post) &&
    !props.isLastPinnedPost &&
    !isPostPinned(postBeingDraggedOver.value)
  ) {
    return false
  }

  // Prevent pinned posts from being dropped between unpinned posts
  if (!isPostPinned(props.post) && isPostPinned(postBeingDraggedOver.value)) return false

  return true
})

// Register the post as a draggable source
addDraggableSource({
  type: TIMELINE_POST_DRAGGABLE_TYPE,
  axis: 'horizontal',
  canDrop: () => canDropPost.value,
  onDragEnter: (e) => {
    if (e.source.data.type !== TIMELINE_POST_DRAGGABLE_TYPE) return
    const post = (e.source.data.data as { post: Post }).post
    postBeingDraggedOver.value = post
  },
  onDragLeave: () => {
    postBeingDraggedOver.value = undefined
  },
  onDrop: (e: OnDropPayload<{ post: Post }, { post: Post }>) => {
    const sourcePost = e.sourceItem.data.post
    const targetPost = e.targetItem.data.post

    // Find the target element
    const targetElement = document.querySelector(`#wish-${targetPost.id}`)
    const postContainer = targetElement?.parentElement
    if (!targetElement || !postContainer) return

    // Get the previous and next post IDs based on the drop position
    const { prevId, nextId } = (() => {
      if (e.relativePositionToTarget === 'before') {
        // We are dropping a post before this one. Find the previous post and its ID.
        const prevPostContainer = postContainer?.previousElementSibling
        const prevPost = prevPostContainer?.querySelector('.timeline-post')
        return {
          prevId: prevPost?.id ?? null, // computePostIndexFromPost differentiates between null and undefined
          nextId: targetElement.id,
        }
      } else {
        // We are dropping a post after this one. Find the next post and its ID.
        const nextPostContainer = postContainer?.nextElementSibling
        const nextPost = nextPostContainer?.querySelector('.timeline-post')
        return {
          prevId: targetElement.id,
          nextId: nextPost?.id ?? null, // computePostIndexFromPost differentiates between null and undefined
        }
      }
    })()

    const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${sourcePost.id}`, {
      prevId,
      nextId,
      sectionId: targetPost.wall_section_id,
    })

    // Make API call to update the post's sort_index
    surfacePostsStore.sortPost({
      postCid: sourcePost.cid,
      sortIndex: sortIndex ?? undefined,
      sectionId: targetPost.wall_section_id,
      gapSize,
    })
  },
})

watch(closestEdgeToPointer, (newClosestEdgeToPointer) => {
  if (!canDropPost.value) return

  emit('closest-edge-to-pointer', newClosestEdgeToPointer)

  if (newClosestEdgeToPointer == null || props.post.cid == null) {
    emit('dashed-line-id-to-hide', null)
    return
  }

  if (newClosestEdgeToPointer === 'end') {
    emit('dashed-line-id-to-hide', props.post.cid)
  } else {
    const currentPostIndex = props.postsInGroup.findIndex((post) => post.id === props.post.id)
    if (currentPostIndex === 0) {
      if (isSectioned.value) {
        emit('dashed-line-id-to-hide', props.post.wall_section_id ?? null)
      } else {
        emit('dashed-line-id-to-hide', null)
      }
    } else {
      const prevPost = props.postsInGroup[currentPostIndex - 1]
      emit('dashed-line-id-to-hide', prevPost.cid)
    }
  }
})
// #endregion
</script>

<template>
  <div
    :id="`wish-${post.id}`"
    ref="containerRef"
    :data-rank="post.sort_index"
    :data-pinned="isPinned"
    :class="[
      'timeline-post', // used by drag n drop sort index calculations
      'relative h-full',
    ]"
  >
    <div
      v-if="!shouldDisableDraggingPost"
      class="absolute bottom-0 pointer-events-none opacity-0"
      style="display: none"
    >
      <!-- Pre-rendered drag preview - hidden but fully processed -->
      <SurfacePostCompressed
        ref="preRenderedDragPreviewRef"
        :post="post"
        :is-timeline-preview="true"
        :style="{
          width: `${isPostSizeWide ? `${NON_STREAM_WIDE_MAX_POST_SIZE}px` : `${NON_STREAM_STANDARD_MAX_POST_SIZE}px`}`,
          rotate: '-1deg',
          transformOrigin: 'center',
        }"
      />
    </div>

    <div class="flex flex-col justify-end items-center gap-4.75 h-full">
      <SurfacePopupWithTip
        ref="surfacePopupWithTipRef"
        :class="[(isPostDragging || isCircleDragging) && 'opacity-50']"
        :color="post.color"
        :should-match-approval-color="shouldMatchApprovalColor(post.published)"
        :style="{
          width: isPostSizeWide ? `${NON_STREAM_WIDE_MAX_POST_SIZE}px` : `${NON_STREAM_STANDARD_MAX_POST_SIZE}px`,
        }"
      >
        <SurfacePost
          :index="index"
          :post="post"
          :put-attachment-on-top="true"
          :x-shadow="false"
          :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] : []"
          :post-comment-ids="post.id ? commentIdsByPostId[post.id] : []"
        />
      </SurfacePopupWithTip>
      <div
        :class="[
          'flex items-center gap-0.5',
          isMobileLandscapeScreen && ['mb-6.5'],
          !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
        ]"
      >
        <SurfaceTimelineDashLine
          v-for="n in dashedLineCount"
          :key="`${post.cid}-start-dashed-line-${n}`"
          :dark-mode="backgroundLuminance === 'dark'"
        />
        <OzPlainButton
          ref="circleButtonRef"
          :aria-label="__('Drag to reorder post')"
          :size-preset="OzPlainButtonSizePreset.Bare"
          :color-scheme="OzPlainButtonColorScheme.Bare"
          :aria-disabled="shouldDisableDraggingPost"
          class="mx-0.5 p-0 rounded-lg focus-visible:ring-2 !cursor-grab aria-disabled:!cursor-default"
          :dark-mode="colorScheme === 'dark'"
        >
          <OzIcon
            class="p-1 opacity-90"
            :size="14"
            :style="{ color: adjustedPrimaryBackgroundColorForCircleAndPlusButton }"
            name="circle_donut_with_shadow"
          />
        </OzPlainButton>
        <SurfaceTimelineDashLine
          v-for="n in dashedLineCount"
          :key="`${post.cid}-end-dashed-line-${n}`"
          :dark-mode="backgroundLuminance === 'dark'"
        />
      </div>
    </div>
    <SurfaceTimelineDropPostIndicatorLine
      v-if="canDropPost && closestEdgeToPointer"
      :is-mobile-landscape-screen="isMobileLandscapeScreen"
      :edge="closestEdgeToPointer"
      :dark-mode="colorScheme === 'dark'"
      :gap="30"
      :dashed-line-margin-percent-from-viewport-bottom="dashedLineMarginPercentFromViewportBottom"
    />
  </div>
</template>
